import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Moon, ShieldCheck, Ban } from "lucide-react";

/** Profile → Halal mode. Turning it on enforces the Shariah rules, it doesn't just filter. */
export const HalalModeCard = () => {
  const [data, setData] = useState(null);
  const [sellOnly, setSellOnly] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [rules, so] = await Promise.all([api.get("/halal/rules"), api.get("/halal/sell-only")]);
    setData(rules.data);
    setSellOnly(so.data.sell_only);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/halal/mode", { islamic_account: on });
      toast[on ? "success" : "info"](on ? "Islamic account on" : "Islamic account off",
        { description: res.message });
      load();
    } catch (e) { toast.error("Couldn't change that", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (!data) return null;

  return (
    <Card className="p-5 shadow-premium" data-testid="halal-mode-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Moon className="h-4 w-4 text-[color:var(--tharwy-indigo)]" />Islamic account
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
            Enforces the Shariah rules rather than just flagging them: only screened instruments,
            no short selling, no margin, and spot-only swap-free currency exchange.
          </p>
        </div>
        <Switch checked={data.islamic_account} onCheckedChange={toggle} disabled={busy}
          data-testid="halal-mode-toggle" />
      </div>

      <div className="mt-4 space-y-2" data-testid="halal-rules-list">
        {data.rules.map((r) => (
          <div key={r.id} className="rounded-lg border px-3 py-2.5" data-testid={`halal-rule-${r.id}`}>
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <ShieldCheck className={`h-3.5 w-3.5 shrink-0 ${data.islamic_account ? "text-emerald-600" : "text-muted-foreground"}`} />
              {r.title}
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{r.detail}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="secondary" className="text-[11px]" data-testid="halal-universe-count">
          {data.screened_universe.toLocaleString()} instruments allowed
        </Badge>
        <Badge variant="secondary" className="text-[11px]" data-testid="halal-blocked-count">
          {data.blocked_count.toLocaleString()} blocked
        </Badge>
        <span>Enforced at {data.enforced_at.join(", ").toLowerCase()}.</span>
      </div>

      {!!sellOnly.length && (        <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3.5" data-testid="halal-sell-only">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <Ban className="h-3.5 w-3.5 text-amber-600" />Sell-only holdings
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            These no longer pass screening. You can exit them whenever you like — we'll never sell
            anything on your behalf.
          </p>
          <div className="mt-2.5 space-y-1.5">
            {sellOnly.map((s) => (
              <div key={`${s.symbol}-so`} className="flex items-baseline justify-between gap-3 text-xs"
                data-testid={`halal-sell-only-${s.symbol}`}>
                <span className="font-medium">{s.name || s.symbol}</span>
                <span className="numeric text-muted-foreground">{s.qty}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
